import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { debounceTime, merge, startWith } from 'rxjs';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { getControl, getFormArray, getGroup } from '@shared/helpers/form.helper';
import { withChangeDebounced } from '@shared/operators/with-change-debounced.operator';
import { Store } from '@ngrx/store';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { cloneDeep } from 'lodash-es';
import { MyVerbale } from '../../../models/interfaces/revisione.interface';
import { toNum } from '@shared/helpers/math.helper';
import { formPaths } from '@shared/constants/form-paths.const';
import { Utilities } from '@core/helpers/utilities';
import { my_NumLiquidatoriListener } from './amm-controllo-liquidatori.listeners';

const calcObbOrganoContrListener = (form: FormGroup, store: Store<Revisione53State>) => {
    const bilancioGroup = getGroup(form, formPaths.bilancio);
    const datiBilancioGroup = getGroup(form, formPaths.datiBilancio);
    const raggScopoGroup = getGroup(form, formPaths.scopoSociale);
    const baseSocialeGroup = getGroup(form, formPaths.baseSociale);
    const idAssegnazioneControl = getControl(form, 'my_IdAssegnazione');
    const ckEnteControllaSocObbRevContControl = getControl(bilancioGroup, 'my_CkEnteControllaSocObbRevCont');
    const ckRedazioneBilancioConsolidatoControl = getControl(bilancioGroup, 'my_CkRedazBilConsol');
    const totaleAttivoAnno1Control = getControl(datiBilancioGroup, 'my_StatoPatrimoniale__Attivo.my_StPaAtTOT1');
    const totaleAttivoAnno2Control = getControl(datiBilancioGroup, 'my_StatoPatrimoniale__Attivo.my_StPaAtTOT2');
    const ricaviVenditeAnno1Control = getControl(raggScopoGroup, 'my_RicaviVendAnno1Dep');
    const ricaviVenditeAnno2Control = getControl(raggScopoGroup, 'my_RicaviVendDep');
    const ulaAnno1Control = getControl(baseSocialeGroup, 'my_ULAPrimo');
    const ulaAnno2Control = getControl(baseSocialeGroup, 'my_ULASecondo');
    const bilancioAnno1Control = getControl(datiBilancioGroup, 'my_BilAnno1');
    const bilancioAnno2Control = getControl(datiBilancioGroup, 'my_BilAnno2');
    const calcObbligoOrganoControlloControl = getControl(form, `${formPaths.ammControllo}.my_CalcObbOrganoContr`);

    return merge(
        idAssegnazioneControl.valueChanges.pipe(startWith(idAssegnazioneControl.value)),
        ckEnteControllaSocObbRevContControl.valueChanges.pipe(startWith(ckEnteControllaSocObbRevContControl.value)),
        ckRedazioneBilancioConsolidatoControl.valueChanges.pipe(startWith(ckRedazioneBilancioConsolidatoControl.value)),
        totaleAttivoAnno1Control.valueChanges.pipe(startWith(totaleAttivoAnno1Control.value)),
        totaleAttivoAnno2Control.valueChanges.pipe(startWith(totaleAttivoAnno2Control.value)),
        ricaviVenditeAnno1Control.valueChanges.pipe(startWith(ricaviVenditeAnno1Control.value)),
        ricaviVenditeAnno2Control.valueChanges.pipe(startWith(ricaviVenditeAnno2Control.value)),
        ulaAnno1Control.valueChanges.pipe(startWith(ulaAnno1Control.value)),
        ulaAnno2Control.valueChanges.pipe(startWith(ulaAnno2Control.value)),
        bilancioAnno1Control.valueChanges.pipe(startWith(bilancioAnno1Control.value)),
        bilancioAnno2Control.valueChanges.pipe(startWith(bilancioAnno2Control.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const idAssegnazione = toNum(idAssegnazioneControl.value);
            const ckEnteControllaSocObbRevCont = ckEnteControllaSocObbRevContControl.value;
            const ckRedazioneBilancioConsolidato = ckRedazioneBilancioConsolidatoControl.value;
            const totaleAttivoAnno1 = toNum(totaleAttivoAnno1Control.value);
            const totaleAttivoAnno2 = toNum(totaleAttivoAnno2Control.value);
            const ricaviVenditeAnno1 = toNum(ricaviVenditeAnno1Control.value);
            const ricaviVenditeAnno2 = toNum(ricaviVenditeAnno2Control.value);
            const ulaAnno1 = toNum(ulaAnno1Control.value);
            const ulaAnno2 = toNum(ulaAnno2Control.value);
            const bilancioAnno1 = toNum(bilancioAnno1Control.value);
            const bilancioAnno2 = toNum(bilancioAnno2Control.value);
            const superaLimitiAnno1 = totaleAttivoAnno1 > 4_000_000 || ricaviVenditeAnno1 > 4_000_000 || ulaAnno1 > 20;
            const superaLimitiAnno2 = totaleAttivoAnno2 > 4_000_000 || ricaviVenditeAnno2 > 4_000_000 || ulaAnno2 > 20;

            const obbligoOrganoControllo =
                idAssegnazione !== 284305 &&
                (ckEnteControllaSocObbRevCont === '1' ||
                    ckRedazioneBilancioConsolidato === '1' ||
                    (superaLimitiAnno1 && bilancioAnno1 > 2020 && superaLimitiAnno2 && bilancioAnno2 > 2020));

            const valoreCalcObbligoOrganoControllo = String(obbligoOrganoControllo).toLowerCase();

            calcObbligoOrganoControlloControl.patchValue(valoreCalcObbligoOrganoControllo, { emitEvent: false });

            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                myVerbaleClone.my_SezioneI__Rilevazione.my_AmministrazioneControllo.my_CalcObbOrganoContr = valoreCalcObbligoOrganoControllo;
                return myVerbaleClone;
            });
        });
};

const subscribeProvinciaComuneCascade = (
    form: FormGroup,
    arrayPath: string,
    provinciaKey: string,
    comuneKey: string
): void => {
    const formArray: FormArray = getFormArray(form, arrayPath);
    if (!formArray) return;

    const subscribedSet = new Set<AbstractControl>();

    const subscribeItem = (control: AbstractControl): void => {
        if (subscribedSet.has(control)) return;
        subscribedSet.add(control);
        const provinciaControl = (control as FormGroup).get(provinciaKey);
        const comuneControl = (control as FormGroup).get(comuneKey);
        if (!provinciaControl || !comuneControl) return;
        provinciaControl.valueChanges
            .pipe(withChangeDebounced(provinciaControl))
            .subscribe(() => comuneControl.setValue(''));
    };

    formArray.controls.forEach(subscribeItem);

    let lastLength = formArray.length;
    formArray.valueChanges.subscribe(() => {
        if (formArray.length !== lastLength) {
            lastLength = formArray.length;
            formArray.controls.forEach(subscribeItem);
        }
    });
};

const listenProvinciaComuneAllArrays = (form: FormGroup): void => {
    const AMM = formPaths.ammControllo;
    subscribeProvinciaComuneCascade(form, `${AMM}.my_AllConsiglioAmministrazione.my_AllElemConsiglioAmministrazione`, 'my_AllConsAmmProvinciaCH', 'my_AllConsAmmComuneCH');
    subscribeProvinciaComuneCascade(form, `${AMM}.my_AllCollegioSindacale.my_AllElemCollegioSindacale`, 'my_AllCollSindProvinciaCH', 'my_AllCollSindComuneCH');
    subscribeProvinciaComuneCascade(form, `${AMM}.my_AllAmminUnico`, 'my_AllAmmUnProvinciaCH', 'my_AllAmmUnComuneCH');
    subscribeProvinciaComuneCascade(form, `${AMM}.my_AllLiquidatori.my_AllElemLiquidatori`, 'my_AllLiquidatoriProvinciaCH', 'my_AllLiquidatoriComuneCH');
    subscribeProvinciaComuneCascade(form, `${AMM}.my_NormeSpaDualistico.my_SpdConsiglioGestione.my_SpdElemConsiglioGestione`, 'my_SpdConsGestProvinciaCH', 'my_SpdConsGestComuneCH');
    subscribeProvinciaComuneCascade(form, `${AMM}.my_NormeSpaMonistico.my_SpmComitatoGestione.my_SpmElemComitatoGestione`, 'my_SpmComitGestProvinciaCH', 'my_SpmComitGestComuneCH');
    subscribeProvinciaComuneCascade(form, `${AMM}.my_NormeSpaDualistico.my_SpdConsiglioSorveglianza.my_SpdElemConsiglioSorveglianza`, 'my_SpdConsSorvProvinciaCH', 'my_SpdConsSorvComuneCH');
};

export const setCommonAmmControlloListeners = (form: FormGroup, store: Store<Revisione53State>): void => {
    my_NumLiquidatoriListener(form, store);
    calcObbOrganoContrListener(form, store);
    listenProvinciaComuneAllArrays(form);
};
